import z from "zod";
import { llmResourcesSchema } from "@/models/llm/resources";
import { definePluginEvents } from "@/plugins/server/builder";
import type { PluginEvent } from "@/plugins/server/types";
import { agentToolRequestSchema, createMessageInputSchema, messageSchema } from "@/shared/messages";

const insertEventBaseSchema = z.object({
  interrupt: z.enum(["abrupt", "smooth"]).or(z.literal(false)).prefault("abrupt"),
  preventInterruption: z.boolean().prefault(false),
});

export const generationPluginEventsDef = definePluginEvents(
  { name: "messages.create", dataSchema: z.object({ message: createMessageInputSchema }) },
  {
    name: "messages.update",
    dataSchema: z.object({
      id: z.string(),
      role: z.enum(["user", "system", "agent", "tool"]),
      message: createMessageInputSchema,
    }),
  },

  { name: "user.audio-chunk", dataSchema: z.object({ audioChunk: z.custom<Int16Array>() }) },
  { name: "user.voice-start" },
  {
    name: "user.voice-chunk",
    dataSchema: z.discriminatedUnion("type", [
      z.object({ type: z.literal("voice"), voiceChunk: z.custom<Int16Array>() }),
      z.object({
        type: z.literal("padding"),
        voiceChunk: z.custom<Int16Array>(),
        paddingSide: z.enum(["pre", "post"]),
        paddingIndex: z.number(),
      }),
    ]),
  },
  { name: "user.voice-end" },
  { name: "user.text-chunk", dataSchema: z.object({ textChunk: z.string() }) },
  { name: "user.interrupted" },

  { name: "agent.thinking-start" },
  { name: "agent.thinking-end" },
  { name: "agent.speaking-start" },
  { name: "agent.speaking-end" },
  { name: "agent.continue", dataSchema: insertEventBaseSchema },
  {
    name: "agent.decide",
    dataSchema: insertEventBaseSchema.extend({ messages: z.array(messageSchema) }),
  },
  {
    name: "agent.say",
    dataSchema: insertEventBaseSchema.extend({ text: z.string() }),
  },
  {
    name: "agent.interrupt",
    dataSchema: z.object({
      reason: z.string(),
      author: z.enum(["user", "application"]),
      force: z.boolean().prefault(false),
    }),
  },
  { name: "agent.resources-request" },
  {
    name: "agent.resources-response",
    dataSchema: z.object({ requestId: z.string(), resources: llmResourcesSchema }),
  },
  {
    name: "agent.tool-requests",
    dataSchema: z.object({ requests: z.array(agentToolRequestSchema) }),
  },
  {
    name: "agent.interrupted",
    dataSchema: z.object({
      reason: z.string(),
      forced: z.boolean(),
      author: z.enum(["user", "application"]),
    }),
  },
  { name: "agent.text-chunk", dataSchema: z.object({ textChunk: z.string() }) },
  { name: "agent.voice-chunk", dataSchema: z.object({ voiceChunk: z.custom<Int16Array>() }) },
);

export type GenerationPluginEvent<T extends "input" | "output"> = PluginEvent<
  typeof generationPluginEventsDef,
  T
>;
